import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../../../api";
import { trackEvent } from "../../../analytics/posthog";
import s from "./SupportPanel.module.css";

const CATEGORIES = [
  { value: "bug", label: "Bug Report" },
  { value: "suggestion", label: "Suggestion" },
  { value: "question", label: "Question" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

function timeAgo(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SupportPanel({ onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [category, setCategory] = useState("question");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/api/v1/support/messages");
      if (!res.ok) throw new Error("Failed to load messages");
      const data = await res.json();
      setMessages(data.messages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mark as read when expanding a replied message
  const handleExpand = useCallback(async (msg) => {
    const newId = expandedId === msg.id ? null : msg.id;
    setExpandedId(newId);
    if (newId && msg.admin_reply && !msg.is_read) {
      try {
        await apiFetch(`/api/v1/support/messages/${msg.id}/read`, { method: "POST" });
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, is_read: true } : m))
        );
        onUnreadChange?.();
      } catch {
        // non-critical
      }
    }
  }, [expandedId, onUnreadChange]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await apiFetch("/api/v1/support/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category, message }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to send message");
      }
      trackEvent("support_ticket_submitted", { category });
      setMessage("");
      setCategory("question");
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <div className={s.headerContent}>
          <div className={s.headerIconWrap}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <div>
            <h2 className={s.headerTitle}>Support</h2>
            <p className={s.headerSub}>Send us a message, we'll respond as soon as possible</p>
          </div>
        </div>
        <button className={s.newBtn} onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "New Message"}
        </button>
      </div>

      <div className={s.scrollArea}>
        {showForm && (
          <form className={s.form} onSubmit={handleSubmit}>
            <div className={s.formRow}>
              <label className={s.label}>Category</label>
              <select className={s.select} value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className={s.formRow}>
              <label className={s.label}>Message</label>
              <textarea
                className={s.textarea}
                rows={4}
                placeholder="Describe your issue or suggestion..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
              />
            </div>
            {error && <p className={s.error}>{error}</p>}
            <button className={s.submitBtn} type="submit" disabled={submitting || !message.trim()}>
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        )}

        {loading ? (
          <div className={s.state}>Loading messages...</div>
        ) : !messages.length ? (
          <div className={s.empty}>
            <p>No messages yet.</p>
            <p className={s.emptyHint}>Click "New Message" to reach out to our support team.</p>
          </div>
        ) : (
          <div className={s.list}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`${s.card} ${msg.admin_reply && !msg.is_read ? s.cardUnread : ""}`}
                onClick={() => handleExpand(msg)}
              >
                <div className={s.cardTop}>
                  <span className={`${s.catBadge} ${s[`cat_${msg.category}`]}`}>
                    {CATEGORY_LABELS[msg.category] || msg.category}
                  </span>
                  <span className={s.cardTime}>{timeAgo(msg.created_at)}</span>
                  {msg.admin_reply && !msg.is_read && <span className={s.unreadDot} />}
                </div>
                <p className={s.cardMsg}>{msg.message}</p>

                {expandedId === msg.id && msg.admin_reply && (
                  <div className={s.reply}>
                    <div className={s.replyLabel}>Support Team</div>
                    <p className={s.replyText}>{msg.admin_reply}</p>
                    <span className={s.replyTime}>{timeAgo(msg.replied_at)}</span>
                  </div>
                )}

                {expandedId === msg.id && !msg.admin_reply && (
                  <p className={s.pending}>Awaiting response...</p>
                )}

                {msg.admin_reply && expandedId !== msg.id && (
                  <p className={s.hasReply}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                    Reply available — click to view
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
